import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { useSyncExternalStore } from 'react';

/*
 * The conversations pinned to the top of the sidebar (D22), in the order the user put them. The
 * server keeps them (`/api/conversations/pins`): every device lists the same pins, and worktree
 * cleanup keeps the worktrees of pinned conversations whichever window runs it. This module holds
 * the copy the page shows, loaded at start and written back on each change.
 */

const PINS_PATH = '/api/conversations/pins';
const NO_PINS: readonly string[] = [];

let pins: readonly string[] = NO_PINS;
let pinSet: ReadonlySet<string> = new Set();
/** Whether the server's pins were loaded (the preview's are seeded instead). */
let loaded = false;
const listeners = new Set<() => void>();

/** A change of the pins, made again on the list the server answers. */
type PinChange = { order: number; apply: (current: readonly string[]) => readonly string[] };
/**
 * The changes the server has not confirmed yet: made before the pins were loaded, or while their
 * write is on its way. Each load makes them again on the server's list, so none is lost.
 */
let unconfirmed: readonly PinChange[] = [];
let changeCount = 0;

function show(next: readonly string[]): void {
  pins = next;
  pinSet = new Set(next);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function change(apply: PinChange['apply']): void {
  changeCount += 1;
  unconfirmed = [...unconfirmed, { order: changeCount, apply }];
  show(apply(pins));
  if (loaded) write();
}

/** Writes the pins shown: the changes they hold are confirmed once the server has them. */
function write(): void {
  const upTo = changeCount;
  void apiFetch(PINS_PATH, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ids: pins }),
  })
    .then(() => {
      unconfirmed = unconfirmed.filter((entry) => entry.order > upTo);
    })
    .catch(() => undefined);
}

/** Loads the pins the server keeps, and answers them: what cleanup must keep, now. */
export async function loadPinnedConversations(): Promise<readonly string[]> {
  const answer = await apiFetch<{ ids: string[] }>(PINS_PATH);
  loaded = true;
  show(unconfirmed.reduce<readonly string[]>((current, entry) => entry.apply(current), answer.ids));
  // What changed before the server answered, or did not reach it, is written now.
  if (unconfirmed.length > 0) write();
  return pins;
}

/** The conversations pinned, at the moment of the call (outside React). */
export function readPinnedConversations(): readonly string[] {
  return pins;
}

export function usePinnedConversations(): ReadonlySet<string> {
  return useSyncExternalStore(
    subscribe,
    () => pinSet,
    () => pinSet,
  );
}

export function togglePinnedConversation(id: string): void {
  // Pinning or unpinning as the user sees it now: made again on the server's list, it does the
  // same whatever that list holds.
  const pin = !pins.includes(id);
  change((current) => {
    const rest = current.filter((entry) => entry !== id);
    return pin ? [id, ...rest] : rest;
  });
}

/**
 * Moves a pinned conversation before another one of Pinned (`beforeId`), or to its end (null):
 * the order the user drags them in.
 */
export function movePinnedConversation(id: string, beforeId: string | null): void {
  if (!pins.includes(id) || id === beforeId) return;
  change((current) => {
    if (!current.includes(id)) return current;
    const rest = current.filter((entry) => entry !== id);
    const at = beforeId === null ? -1 : rest.indexOf(beforeId);
    return at === -1 ? [...rest, id] : [...rest.slice(0, at), id, ...rest.slice(at)];
  });
}

/** Pins these conversations while none are (the preview's, which has no server). */
export function seedPinnedConversations(ids: readonly string[]): void {
  if (pins.length === 0) show(ids);
}

/** Forgets the pins, for tests. */
export function resetPinnedConversations(): void {
  loaded = false;
  unconfirmed = [];
  changeCount = 0;
  show(NO_PINS);
}
